import React, { useEffect, useRef } from 'react';
import { LOGO_IMAGES } from '@/assets/images/floating';

interface FloatingLogoProps {
  x: number;
  y: number;
  imageIndex: number;
  onAnimationComplete: () => void;
}

export default function FloatingLogo({ x, y, imageIndex, onAnimationComplete }: FloatingLogoProps) {
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = logoRef.current;
    if (!element) return;

    const angle = Math.random() * Math.PI * 2;
    const distance = Math.min(window.innerWidth, window.innerHeight) * 0.2;
    const targetX = x + Math.cos(angle) * distance;
    const targetY = y + Math.sin(angle) * distance;
    const rotation = Math.random() * 720 - 360;

    requestAnimationFrame(() => {
      element.style.transition = 'none';
      element.style.transform = `translate(${x}px, ${y}px) scale(0.2) rotate(0deg)`;
      element.style.opacity = '1';

      requestAnimationFrame(() => {
        element.style.transition = 'all 2s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.transform = `translate(${targetX}px, ${targetY}px) scale(1) rotate(${rotation}deg)`;
        element.style.opacity = '0';
      });
    });

    const timer = setTimeout(onAnimationComplete, 2000);
    return () => clearTimeout(timer);
  }, [x, y, onAnimationComplete]);

  // Adjusted size calculation for mobile
  const size = Math.min(Math.max(window.innerWidth * 0.15, 96), 160);

  return (
    <div
      ref={logoRef}
      className="fixed pointer-events-none -translate-x-1/2 -translate-y-1/2 z-50 will-change-transform"
      style={{ 
        position: 'fixed',
        left: 0,
        top: 0,
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <img 
        src={LOGO_IMAGES[imageIndex % LOGO_IMAGES.length]}
        alt="NodeRock Logo"
        className="w-full h-full object-contain"
        draggable="false"
        loading="eager"
      />
    </div>
  );
}

export { LOGO_IMAGES };